import React, { useEffect, useRef, useState } from 'react';

/**
 * ServerStatusOverlay
 *
 * Watches the Lyric backend with a lightweight heartbeat and, when the server
 * stops responding (e.g. during an "apply-and-restart" rollout), replaces the
 * raw ALB "503 Service Temporarily Unavailable" experience with a friendly
 * full-screen "the site is coming back up" message. It auto-clears as soon as
 * the backend answers again.
 *
 * Notes / limitations:
 *  - The heartbeat targets /api/version, which is unauthenticated, cheap, and
 *    (in prod) routed by the ALB to the backend container (:3001). It is up
 *    within seconds of a pod restart, well before the frontend dev server
 *    finishes compiling, so it is the earliest reliable "we're back" signal.
 *  - This only helps while the SPA is already loaded in the browser (the common
 *    case when a user clicks apply-and-restart and watches the page). A HARD
 *    refresh during total downtime is still served by the ALB itself and would
 *    need an edge/CDN maintenance page to intercept.
 */

const HEALTH_URL = '/api/version';
const POLL_OK_MS = 20000;      // relaxed cadence while healthy
const POLL_DOWN_MS = 3000;     // faster cadence while reconnecting
const REQUEST_TIMEOUT_MS = 6000;
const FAILURES_BEFORE_SHOW = 2; // ignore a single transient blip
const STYLE_ID = 'lyric-server-status-overlay-style';

function ensureStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes lyricSpin { to { transform: rotate(360deg); } }
    @keyframes lyricFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .lyric-status-overlay {
      position: fixed; inset: 0; z-index: 2147483000;
      display: flex; align-items: center; justify-content: center;
      background: rgba(15, 23, 42, 0.72);
      backdrop-filter: blur(3px);
      animation: lyricFadeIn 220ms ease-out;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }
    .lyric-status-card {
      max-width: 440px; width: calc(100% - 48px);
      background: #ffffff; color: #0f172a;
      border-radius: 16px; padding: 32px 28px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.35);
      text-align: center;
    }
    .lyric-status-spinner {
      width: 46px; height: 46px; margin: 0 auto 18px;
      border: 4px solid #e2e8f0; border-top-color: #2563eb;
      border-radius: 50%; animation: lyricSpin 900ms linear infinite;
    }
    .lyric-status-title { font-size: 20px; font-weight: 700; margin: 0 0 8px; }
    .lyric-status-msg { font-size: 14.5px; line-height: 1.5; color: #475569; margin: 0 0 6px; }
    .lyric-status-sub { font-size: 12.5px; color: #94a3b8; margin: 10px 0 0; }
    .lyric-status-actions { margin-top: 20px; }
    .lyric-status-btn {
      appearance: none; border: 1px solid #cbd5e1; background: #f8fafc;
      color: #334155; font-size: 13px; font-weight: 600;
      padding: 8px 16px; border-radius: 9px; cursor: pointer;
    }
    .lyric-status-btn:hover { background: #eef2f7; }
    .lyric-status-card.recovered .lyric-status-spinner {
      border-top-color: #16a34a; animation: none;
      border-color: #16a34a; position: relative;
    }
  `;
  document.head.appendChild(el);
}

const ServerStatusOverlay = () => {
  // 'online' | 'reconnecting' | 'recovered'
  const [status, setStatus] = useState('online');
  const failuresRef = useRef(0);
  const statusRef = useRef('online');
  const timerRef = useRef(null);
  const recoveredTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => { statusRef.current = status; }, [status]);

  useEffect(() => {
    ensureStyle();
    mountedRef.current = true;

    const schedule = () => {
      const delay = statusRef.current === 'reconnecting' ? POLL_DOWN_MS : POLL_OK_MS;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(runProbe, delay);
    };

    const markDown = () => {
      failuresRef.current += 1;

      // If we were briefly "recovered" but the backend flaps, drop back into reconnecting immediately.
      if (statusRef.current === 'recovered') {
        failuresRef.current = FAILURES_BEFORE_SHOW;
        if (recoveredTimerRef.current) clearTimeout(recoveredTimerRef.current);
        statusRef.current = 'reconnecting';
        setStatus('reconnecting');
        return;
      }

      if (failuresRef.current >= FAILURES_BEFORE_SHOW && statusRef.current === 'online') {
        statusRef.current = 'reconnecting';
        setStatus('reconnecting');
      }
    };

    const markUp = () => {
      failuresRef.current = 0;
      if (statusRef.current === 'reconnecting') {
        // Was down and is now back — celebrate briefly, then clear.
        setStatus('recovered');
        recoveredTimerRef.current = setTimeout(() => {
          if (mountedRef.current) setStatus('online');
        }, 2200);
      } else if (statusRef.current === 'recovered') {
        // stay in recovered until its timer clears it
      } else {
        setStatus('online');
      }
    };

    const runProbe = async () => {
      if (!mountedRef.current) return;
      const controller = new AbortController();
      const to = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(HEALTH_URL, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
          headers: { 'Accept': 'application/json' }
        });
        // Any response < 500 means the backend is answering. 502/503/504 (or a
        // thrown network error) are the rollout / no-healthy-target signals.
        if (res.status >= 500) markDown();
        else markUp();
      } catch (_err) {
        markDown();
      } finally {
        clearTimeout(to);
        if (mountedRef.current) schedule();
      }
    };

    // Recheck immediately when the tab regains focus / network returns.
    const onWake = () => {
      if (!mountedRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      runProbe();
    };
    window.addEventListener('online', onWake);
    window.addEventListener('focus', onWake);

    schedule();

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (recoveredTimerRef.current) clearTimeout(recoveredTimerRef.current);
      window.removeEventListener('online', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  if (status === 'online') return null;

  const recovered = status === 'recovered';

  return (
    <div className="lyric-status-overlay" role="alert" aria-live="assertive">
      <div className={`lyric-status-card${recovered ? ' recovered' : ''}`}>
        <div className="lyric-status-spinner" aria-hidden="true">
          {recovered ? (
            <span style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: '#16a34a', fontSize: 24, fontWeight: 700
            }}>✓</span>
          ) : null}
        </div>
        {recovered ? (
          <>
            <p className="lyric-status-title">Back online</p>
            <p className="lyric-status-msg">Lyric is up and running again.</p>
          </>
        ) : (
          <>
            <p className="lyric-status-title">Lyric is updating…</p>
            <p className="lyric-status-msg">
              The site is coming back up after a restart. This page will refresh
              itself automatically the moment it&apos;s ready — no need to reload.
            </p>
            <p className="lyric-status-sub">Reconnecting…</p>
            <div className="lyric-status-actions">
              <button
                type="button"
                className="lyric-status-btn"
                onClick={() => window.location.reload()}
              >
                Reload now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServerStatusOverlay;
